/**
 * Everything required to reach one /admin route.
 *
 * All fields are ANDed. This is the single source of truth for both route
 * access and sidebar visibility, so the two cannot drift: the sidebar items
 * (rspSideBarItems / cspSideBarItems) look their access up from the descriptor
 * table by `link`, and the route table (admin's app/Main.tsx) guards each leaf
 * by the same key. The table lives in
 * `@rsos/capstone/src/constants/adminRouteAccess`; the shape lives here, next to
 * the predicate that reads it, so the predicate stays independent of the table.
 */
export interface AccessDescriptor {
  /**
   * Permission the user's role must hold: the one that describes what the page
   * does. Always drawn from the `rsp_rbac: true` set in
   * capstone_permissions.yaml — those are the admin page-access permissions, and
   * they are mirrored in `@rsos/constants/pageAccess`.
   *
   * Some write endpoints accept more than one permission, because scorpius
   * authorizes with the any-of `authorize_multiple_permissions`. That is not a
   * reason to widen a route: the extra permissions there are backwards
   * compatibility, and a page should ask for the permission that is about the
   * page.
   */
  permission: string;
  /** Org capability that must be active. */
  capabilityAccess?: string;
  /** Feature flag that must be on. */
  flagAccess?: string;
  /** Feature flag that hides the route while it is on. */
  flagNotAccess?: string;
  /**
   * Applications allowed to reach the route. Omitted means "any admin
   * application". Needed wherever a route serves one of RSP/CSP and not the
   * other: nothing else in the permission model expresses that, so without it
   * the only thing keeping an RSP-only page from a CSP admin is that the CSP
   * sidebar does not link to it — which a typed URL ignores.
   */
  applications?: string[];
}

export interface AccessContext {
  /**
   * Permission names granted to the user's current role
   * (sinatra.user.currentRole.permissions).
   */
  permissions: string[];
  /**
   * Flag map from OpenFeature `useFlags()` — values are whatever type Datadog
   * serves (boolean / string / number / object), not just booleans. `hasAccess`
   * only truthy-checks a named flag, so any value type works.
   */
  flags: Record<string, boolean | string | number | object>;
  activeCapabilities: Record<string, unknown>;
  /** sinatra.user.currentRole.application */
  application?: string;
}

/**
 * The one predicate behind both route access and sidebar visibility. Every
 * field of the descriptor is ANDed; see AccessDescriptor for what each means.
 */
export const hasAccess = (
  descriptor: AccessDescriptor | undefined,
  { permissions, flags, activeCapabilities, application }: AccessContext,
): boolean => {
  // No descriptor means nothing has declared how to reach the route, so there
  // is no rule to satisfy. Fail closed.
  if (!descriptor) return false;

  const {
    permission,
    capabilityAccess,
    flagAccess,
    flagNotAccess,
    applications,
  } = descriptor;

  // Flag names are declared in either case; check both, as the sidebar always
  // has.
  const isFlagOn = (flag: string) =>
    !!(flags[flag] || flags[flag.toLowerCase()]);

  if (flagAccess && !isFlagOn(flagAccess)) return false;
  if (flagNotAccess && isFlagOn(flagNotAccess)) return false;
  if (capabilityAccess && !(capabilityAccess in activeCapabilities)) {
    return false;
  }
  if (applications && (!application || !applications.includes(application))) {
    return false;
  }

  return permissions.includes(permission);
};

export default hasAccess;
